import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchNotificationPreferences,
  patchNotificationPreferences,
} from "./notificationPreferencesApi";
import {
  buildPreferencePatch,
  clonePreferences,
  detectDeviceTimezone,
  validatePreferenceDraft,
} from "./notificationPreferencesConfig";
import { NOTIFICATION_PREFERENCES_REFRESH_EVENT } from "./notificationPreferencesEvents";

const INITIAL_STATE = {
  status: "idle",
  saved: null,
  draft: null,
  metadata: null,
  loadError: null,
  saveError: null,
  savedSuccessfully: false,
};

export default function useNotificationPreferences({ accountKey }) {
  const [state, setState] = useState(INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [detectedTimezone, setDetectedTimezone] = useState(null);
  const accountRef = useRef(accountKey);
  const accountVersionRef = useRef(0);
  const loadSequenceRef = useRef(0);
  const saveSequenceRef = useRef(0);
  const loadAbortRef = useRef(null);
  const saveAbortRef = useRef(null);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const requestedAccount = accountRef.current;
    if (!requestedAccount) return;

    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    const requestSequence = ++loadSequenceRef.current;
    const accountVersion = accountVersionRef.current;

    setState((current) => ({
      ...current,
      status: "loading",
      loadError: null,
      savedSuccessfully: false,
    }));

    try {
      const payload = await fetchNotificationPreferences({
        signal: controller.signal,
      });
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        requestSequence !== loadSequenceRef.current ||
        accountVersion !== accountVersionRef.current ||
        requestedAccount !== accountRef.current
      ) {
        return;
      }
      setState({
        status: "ready",
        saved: clonePreferences(payload.preferences),
        draft: clonePreferences(payload.preferences),
        metadata: {
          availableModes: [...payload.availableModes],
          categories: [...payload.categories],
          policyVersion: payload.policyVersion,
        },
        loadError: null,
        saveError: null,
        savedSuccessfully: false,
      });
    } catch (error) {
      if (!mountedRef.current || controller.signal.aborted) return;
      setState((current) => ({
        ...current,
        status: "error",
        saved: null,
        draft: null,
        metadata: null,
        loadError: error?.code || "notification_preferences_load_error",
        saveError: null,
        savedSuccessfully: false,
      }));
    }
  }, []);

  useEffect(() => {
    accountRef.current = accountKey || null;
    accountVersionRef.current += 1;
    loadSequenceRef.current += 1;
    saveSequenceRef.current += 1;
    loadAbortRef.current?.abort();
    saveAbortRef.current?.abort();
    savingRef.current = false;
    setSaving(false);
    setState(INITIAL_STATE);
    setDetectedTimezone(detectDeviceTimezone());

    if (accountKey) load();

    return () => {
      loadAbortRef.current?.abort();
      saveAbortRef.current?.abort();
    };
  }, [accountKey, load]);

  useEffect(() => {
    const handleRegistrationRefresh = () => {
      if (accountRef.current) load();
    };
    window.addEventListener(
      NOTIFICATION_PREFERENCES_REFRESH_EVENT,
      handleRegistrationRefresh
    );
    return () =>
      window.removeEventListener(
        NOTIFICATION_PREFERENCES_REFRESH_EVENT,
        handleRegistrationRefresh
      );
  }, [load]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadAbortRef.current?.abort();
      saveAbortRef.current?.abort();
    };
  }, []);

  const updateDraft = useCallback((updater) => {
    setState((current) => {
      if (!current.draft) return current;
      const nextDraft = clonePreferences(current.draft);
      updater(nextDraft);
      return {
        ...current,
        draft: nextDraft,
        saveError: null,
        savedSuccessfully: false,
      };
    });
  }, []);

  const patch = useMemo(
    () => buildPreferencePatch(state.saved, state.draft),
    [state.saved, state.draft]
  );
  const isDirty = Object.keys(patch).length > 0;
  const validationError = useMemo(
    () =>
      state.draft
        ? validatePreferenceDraft(
            state.draft,
            state.metadata?.availableModes
          )
        : null,
    [state.draft, state.metadata]
  );

  const save = useCallback(async () => {
    if (
      savingRef.current ||
      !accountRef.current ||
      !isDirty ||
      validationError
    ) {
      return false;
    }

    savingRef.current = true;
    setSaving(true);
    const controller = new AbortController();
    saveAbortRef.current?.abort();
    saveAbortRef.current = controller;
    const requestSequence = ++saveSequenceRef.current;
    const accountVersion = accountVersionRef.current;
    const requestedAccount = accountRef.current;
    setState((current) => ({
      ...current,
      saveError: null,
      savedSuccessfully: false,
    }));

    try {
      const payload = await patchNotificationPreferences(patch, {
        signal: controller.signal,
      });
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        requestSequence !== saveSequenceRef.current ||
        accountVersion !== accountVersionRef.current ||
        requestedAccount !== accountRef.current
      ) {
        return false;
      }
      setState((current) => ({
        ...current,
        status: "ready",
        saved: clonePreferences(payload.preferences),
        draft: clonePreferences(payload.preferences),
        saveError: null,
        savedSuccessfully: true,
      }));
      return true;
    } catch (error) {
      if (!mountedRef.current || controller.signal.aborted) return false;
      setState((current) => ({
        ...current,
        saveError: error?.code || "notification_preferences_save_error",
        savedSuccessfully: false,
      }));
      return false;
    } finally {
      if (
        mountedRef.current &&
        requestSequence === saveSequenceRef.current &&
        accountVersion === accountVersionRef.current
      ) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }, [isDirty, patch, validationError]);

  const actions = useMemo(
    () => ({
      retry: load,
      save,
      setCategoryMode: (category, mode) =>
        updateDraft((draft) => {
          draft.categoryModes[category] = mode;
        }),
      setPreviewsEnabled: (enabled) =>
        updateDraft((draft) => {
          draft.previewsEnabled = enabled;
        }),
      setQuietHoursField: (field, value) =>
        updateDraft((draft) => {
          draft.quietHours[field] = value;
        }),
      setLocale: (locale) =>
        updateDraft((draft) => {
          draft.locale = locale;
        }),
      useDetectedTimezone: () => {
        if (!detectedTimezone) return;
        updateDraft((draft) => {
          draft.quietHours.timezone = detectedTimezone;
        });
      },
      clearTimezone: () =>
        updateDraft((draft) => {
          draft.quietHours.timezone = null;
        }),
    }),
    [detectedTimezone, load, save, updateDraft]
  );

  return {
    ...state,
    saving,
    detectedTimezone,
    patch,
    isDirty,
    validationError,
    actions,
  };
}
